import * as yup from 'yup';

/**
 * E37 · Diary card — the both-ends input codecs of the diary-card mutations (plan 03 §2.1 #3): the phone, the portal and —
 * as the shield's inputRule — the API run the SAME schemas. SHAPE only; the business rules (a rating's range, a day older
 * than 7 days, a row on no card, a second card, who may fill) are the deciders', where they are binding. `subject`
 * (except where the therapist names the client), `performer`, `practiceId` are ABSENT on purpose: the caller is the
 * performer and their practice rides from the session.
 */
const userRef = yup
  .string()
  .trim()
  .matches(/^users\/[A-Za-z0-9_-]{1,64}$/);

const uuid = yup
  .string()
  .trim()
  .matches(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);

const day = yup
  .string()
  .trim()
  .matches(/^\d{4}-\d{2}-\d{2}$/);

const target = yup.object({
  targetId: yup.string().trim().min(1).max(64).required(),
  name: yup.string().trim().min(1).max(120).required(),
  kind: yup.string().oneOf(['urge', 'scale', 'action', 'count', 'text']).required(),
  max: yup.number().integer().oneOf([5, 10]).nullable(),
  countable: yup.boolean().nullable(),
  standard: yup.boolean().nullable(),
  faTargetId: yup.string().trim().max(64).nullable(),
});

const emotionRow = yup.object({ id: yup.string().trim().min(1).max(40).required() });
const skillRow = yup.object({
  skillId: yup.string().trim().min(1).max(40).required(),
  module: yup.string().trim().min(1).max(40).required(),
});

const rows = {
  targets: yup.array().of(target.required()).max(60).required(),
  emotions: yup.array().of(emotionRow.required()).max(60).nullable(),
  skills: yup.array().of(skillRow.required()).max(60).nullable(),
};

const rating = yup.object({
  targetId: yup.string().trim().min(1).max(64).required(),
  cardId: uuid.nullable(),
  max: yup.number().integer().nullable(),
  urge: yup.number().integer().nullable(),
  scale: yup.number().integer().nullable(),
  acted: yup.boolean().nullable(),
  count: yup.number().integer().nullable(),
  text: yup.string().max(2000).nullable(),
});

const fillContent = {
  fillId: uuid.required(),
  day: day.required(),
  ratings: yup.array().of(rating.required()).max(120).nullable(),
  emotionRatings: yup
    .array()
    .of(
      yup
        .object({
          id: yup.string().trim().min(1).max(40).required(),
          rating: yup.number().integer().required(),
        })
        .required(),
    )
    .max(60)
    .nullable(),
  skillsUsed: yup.number().integer().nullable(),
  skillsPracticed: yup.array().of(yup.string().max(40).required()).max(60).nullable(),
  note: yup.string().max(4000).nullable(),
  timeZone: yup.string().trim().max(64).nullable(),
};

export const publishDiaryCardTemplateInputSchema = yup.object({
  templateId: yup.string().trim().matches(/^[A-Za-z][A-Za-z0-9_-]{1,48}$/).required(),
  name: yup.string().trim().min(1).max(120).required(),
  summary: yup.string().trim().min(1).max(400).required(),
  ...rows,
});

export const defineDiaryCardInputSchema = yup.object({
  subject: userRef.nullable(),
  cardId: uuid.nullable(),
  template: yup
    .string()
    .oneOf(['dbtStandard', 'dbtSkills', 'eatingDay', 'drinkingDay', 'custom'])
    .required(),
  ...rows,
});

export const fillDiaryCardInputSchema = yup.object(fillContent);

export const fillDiaryCardInSessionInputSchema = yup.object({
  ...fillContent,
  subject: userRef.required(),
});

export const publishDiaryCardTemplateArgsSchema = yup.object({
  input: publishDiaryCardTemplateInputSchema.required(),
});
export const defineDiaryCardArgsSchema = yup.object({
  input: defineDiaryCardInputSchema.required(),
});
export const fillDiaryCardArgsSchema = yup.object({
  input: fillDiaryCardInputSchema.required(),
});
export const fillDiaryCardInSessionArgsSchema = yup.object({
  input: fillDiaryCardInSessionInputSchema.required(),
});

// Typed loosely on purpose: `getValidators` spreads every context's codecs into ONE inferred object, and with these eight
// spelled out in full it passes the compiler's serialisation limit (TS7056). The shape is the Yup schemas above.
export const diaryCardValidators: Record<
  | 'publishDiaryCardTemplateInputSchema'
  | 'defineDiaryCardInputSchema'
  | 'fillDiaryCardInputSchema'
  | 'fillDiaryCardInSessionInputSchema'
  | 'publishDiaryCardTemplateArgsSchema'
  | 'defineDiaryCardArgsSchema'
  | 'fillDiaryCardArgsSchema'
  | 'fillDiaryCardInSessionArgsSchema',
  yup.AnyObjectSchema
> = {
  publishDiaryCardTemplateInputSchema,
  defineDiaryCardInputSchema,
  fillDiaryCardInputSchema,
  fillDiaryCardInSessionInputSchema,
  publishDiaryCardTemplateArgsSchema,
  defineDiaryCardArgsSchema,
  fillDiaryCardArgsSchema,
  fillDiaryCardInSessionArgsSchema,
};
